import type { GridApi, Theme } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React from 'react';
import { useMemberSubsetGrid } from 'src/components/star-grid/hooks/subset-editor/useMemberSubsetGrid';
import type { MemberRow, MemberSubsetOp } from 'src/components/star-grid/utils/subset-editor/memberSubset';
import { SUBSET_GRID_ICONS } from 'src/components/star-grid/utils/subset-editor/memberSubsetGridConfig';
import './styles/member-subset-grid.scss';

export interface MemberSubsetGridProps {
  readonly rows: readonly MemberRow[];
  readonly allRows?: readonly MemberRow[];
  readonly multiple?: boolean;
  /** False ticks only the row clicked: the add-row modal builds a line set of exactly what was ticked. */
  readonly cascade?: boolean;
  readonly theme: Theme;
  /** Position keys currently ticked. */
  readonly checked: ReadonlySet<string>;
  /** Receives the whole checked set: ticking a parent cascades to its descendants. */
  readonly onCheckedChanged: (keys: readonly string[]) => void;
  /** Reports the grid's expansion back, so the caller can derive what is visible. */
  readonly onExpandedChanged: (key: string, expanded: boolean) => void;
  /** Which rows start open — the operation decides this, not the previous browsing state. */
  readonly initiallyExpanded: ReadonlySet<string>;
  readonly onRunOp: (op: MemberSubsetOp, selected: readonly string[]) => void;
  /** Takes ROW UIDS: `Parent` puts one position on screen twice, so a key cannot name which row to remove. */
  readonly onRemove: (uids: readonly string[], withDescendants: boolean) => void;
  readonly onAddParents: (clickedKey: string) => void;
  /** Expanding a row the grid sees as a leaf: insert the member's children into the set. */
  readonly onDrill: (uid: string, key: string) => void;
  /** Hands the grid API up, so Select all / Clear can drive the grid's own selection. */
  readonly onApiReady?: (api: GridApi<MemberRow>) => void;
  readonly testId?: string;
}

/**
 * The member tree as an AG Grid, for virtualization: only the rows in view reach the DOM, so it is handed
 * the operation's FULL row set and owns expand/collapse. Handing over only the visible rows broke
 * re-expansion, because a collapsed node lost its children and with them its expander.
 */
const MemberSubsetGrid: React.FC<MemberSubsetGridProps> = ({
  rows,
  allRows = rows,
  multiple = true,
  cascade = true,
  theme,
  checked,
  onCheckedChanged,
  onExpandedChanged,
  initiallyExpanded,
  onRunOp,
  onRemove,
  onAddParents,
  onDrill,
  onApiReady,
  testId
}) => {
  const grid = useMemberSubsetGrid({
    rows,
    allRows,
    multiple,
    cascade,
    theme,
    checked,
    onCheckedChanged,
    onExpandedChanged,
    initiallyExpanded,
    onRunOp,
    onRemove,
    onAddParents,
    onDrill,
    onApiReady
  });

  return (
    <div className="sg-subset-grid" ref={grid.setPopupParent} data-testid={testId}>
      <AgGridReact<MemberRow>
        rowData={rows as MemberRow[]}
        getRowId={grid.getRowId}
        columnDefs={grid.columnDefs}
        autoGroupColumnDef={grid.autoGroupColumnDef}
        treeData
        getDataPath={grid.getDataPath}
        isGroupOpenByDefault={grid.isGroupOpenByDefault}
        // `isRowMaster` makes a childless row expandable; its detail row is suppressed because expanding it drills.
        masterDetail
        isRowMaster={grid.isRowMaster}
        detailRowHeight={0}
        detailCellRenderer={grid.detailCellRenderer}
        // `enableSelection` makes shift-click ranges work without letting a stray click clear a long selection.
        rowSelection={
          multiple
            ? {
                mode: 'multiRow',
                checkboxes: true,
                headerCheckbox: false,
                groupSelects: cascade ? 'descendants' : 'self',
                enableClickSelection: 'enableSelection'
              }
            : { mode: 'singleRow', checkboxes: true, enableClickSelection: true }
        }
        onSelectionChanged={grid.onSelectionChanged}
        onCellKeyDown={grid.onCellKeyDown}
        onFirstDataRendered={grid.onFirstDataRendered}
        onModelUpdated={grid.onModelUpdated}
        onRowGroupOpened={grid.onRowGroupOpened}
        icons={SUBSET_GRID_ICONS}
        getContextMenuItems={grid.getContextMenuItems}
        rowHeight={26}
        headerHeight={26}
        theme={grid.gridTheme}
        popupParent={grid.popupParent ?? undefined}
      />
    </div>
  );
};

export default MemberSubsetGrid;
